import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLock, FaMicrosoft, FaExternalLinkAlt } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useMsal } from '@azure/msal-react';
import { redirectRequest } from '../config/msalConfig';
import styles from './LoginForm.module.css';

const LoginForm: React.FC = () => {
  const { user, login, isLoading, error, clearError } = useAuth();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    keepLoggedIn: false
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateForm = (): boolean => {
    console.log('Validating form with data:', formData);
    const newErrors: { [key: string]: string } = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
      console.log('Validation error: Email is required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
      console.log('Validation error: Invalid email format');
    } else {
      console.log('Email validation passed');
    }

    console.log('Validation result:', { errors: newErrors, isValid: Object.keys(newErrors).length === 0 });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('=== LOGIN FORM SUBMISSION STARTED ===');
    console.log('Login form submitted with data:', formData);
    console.log('Current window location:', window.location.href);
    // console.log('Current localStorage state:', {
    //   hasUser: !!localStorage.getItem('user'),
    //   hasToken: !!localStorage.getItem('azureIdToken'),
    //   hasAccessToken: !!localStorage.getItem('azureAccessToken')
    // });
    // console.log('Current sessionStorage state:', {
    //   keys: Object.keys(sessionStorage),
    //   length: sessionStorage.length
    // });
    // console.log('MSAL instance state:', {
    //   isInitialized: !!instance.getConfiguration(),
    //   accounts: instance.getAllAccounts().length
    // });
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    try {
      console.log('Calling login function...');
      // console.log('About to call login with credentials:', {
      //   email: formData.email,
      //   password: '', // Not used in SSO, but required by interface
      //   keepLoggedIn: formData.keepLoggedIn
      // });
      
      await login({
        email: formData.email,
        password: '', // Not used in SSO, but required by interface
        keepLoggedIn: formData.keepLoggedIn
      });
      
      console.log('Login function completed successfully');
      // console.log('User state after login:', user);
      
    } catch (error) {
      console.error('SSO login failed:', error);
      console.error('Error details:', {
        name: (error as any).name,
        message: (error as any).message,
        errorCode: (error as any).errorCode,
        errorMessage: (error as any).errorMessage
      });
      // Error is already set in the context
    }
    console.log('=== LOGIN FORM SUBMISSION ENDED ===');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleRedirectLogin = async () => {
    try {
      console.log('Starting redirect login...');
      console.log('Current window location:', window.location.href);
      console.log('Current origin:', window.location.origin);
      clearError();
      await instance.loginRedirect(redirectRequest);
      console.log('Redirect login initiated');
    } catch (error) {
      console.error('Redirect login failed:', error);
    }
  };

  const handleClearCache = () => {
    console.log('Manually clearing all cache...');
    localStorage.clear();
    sessionStorage.clear();
    
    // Also clear MSAL accounts
    try {
      const accounts = instance.getAllAccounts();
      console.log('Found MSAL accounts:', accounts.length);
      if (accounts.length > 0) {
        console.log('Clearing MSAL accounts...');
        instance.clearCache();
      }
    } catch (error) {
      console.error('Error clearing MSAL cache:', error);
    }
    
    console.log('Cache cleared manually');
    alert('Cache cleared! Try logging in again.');
  };

  return (
    <div className={styles.loginForm}>
      <div className={styles.header}>
        <div className={styles.logoContainer}>
          <FaLock className={styles.lockIcon} />
          <h1 className={styles.appName}>Policy.net</h1>
        </div>
        <p className={styles.subtitle}>Sign in to your account</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.inputGroup}>
          <div className={styles.inputWrapper}>
            <FaEnvelope className={styles.inputIcon} />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleInputChange}
              className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
            />
          </div>
          {errors.email && <span className={styles.errorText}>{errors.email}</span>}
        </div>

        {/* <div className={styles.ssoInfo}>
          <div className={styles.ssoIcon}>
            <FaMicrosoft />
          </div>
          <p className={styles.ssoText}>
            You will be redirected to Microsoft Azure AD for secure authentication
          </p>
        </div> */}

        <div className={styles.checkboxGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="keepLoggedIn"
              checked={formData.keepLoggedIn}
              onChange={handleInputChange}
              className={styles.checkbox}
            />
            <span className={styles.checkboxText}>Keep me logged in</span>
          </label>
        </div>

        <button
          type="submit"
          className={styles.loginButton}
          disabled={isLoading}
        >
          {isLoading ? 'Signing in...' : 'Sign in'}
        </button>
      </form>

      {/* Fallback redirect login button - always available */}
      {/* <div className={styles.fallbackSection}>
        <div className={styles.divider}>
          <span>or</span>
        </div>
        <button
          type="button"
          className={styles.redirectButton}
          onClick={handleRedirectLogin}
          disabled={isLoading}
        >
          <FaExternalLinkAlt className={styles.buttonIcon} />
          Try Alternative Sign-in Method
        </button>
        <p className={styles.fallbackText}>
          If popup authentication isn't working, try this alternative method.
        </p> */}
        
        {/* <button
          type="button"
          className={styles.redirectButton}
          onClick={handleClearCache}
          disabled={isLoading}
          style={{ marginTop: '10px', backgroundColor: '#ff6b6b' }}
        >
          Clear Cache & Retry
        </button>
        <p className={styles.fallbackText}>
          If you're having trouble logging in after logout, try clearing the cache.
        </p> */}
        
        {/* <button
          type="button"
          className={styles.redirectButton}
          onClick={() => {
            console.log('=== DEBUG INFO ===');
            // console.log('Current user state:', user);
            // console.log('localStorage user:', localStorage.getItem('user'));
            // console.log('localStorage token:', localStorage.getItem('azureIdToken'));
            // console.log('MSAL accounts:', instance.getAllAccounts());
            // console.log('Current URL:', window.location.href);
            // console.log('==================');
            // alert('Debug info logged to console. Check browser console for details.');
          }}
          disabled={isLoading}
          style={{ marginTop: '10px', backgroundColor: '#17a2b8' }}
        >
          Debug Auth State
        </button>
      </div> */}

      {/* <div className={styles.divider}>
        <span>or</span>
      </div> */}

      {/* <GoogleLoginButton /> */}

      <div className={styles.registerLink}>
        <p>
          Don't have an account?{' '}
          <button 
            type="button" 
            className={styles.link} 
            onClick={() => navigate('/signup')}
          >
            Request access to Policy.net
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
